import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {ArrowRight, Globe, Plane, Users, Briefcase, Heart, Mountain} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const TripTypes = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const tripCategories = [
    {
      id: "hajj-umrah",
      title: "Hajj & Umrah",
      icon: <Globe className="w-12 h-12" />,
      description: "Specialized health planning for pilgrimage to Saudi Arabia with crowd health considerations.",
      keyRisks: ["Meningococcal Disease", "Heat Exhaustion", "Respiratory Infections"],
      image: "https://static.lumi.new/11/11d13f9361eca0e59556dc8c40d07c85.jpeg",
      gradient: "from-amber-500 to-orange-600"
    },
    {
      id: "southeast-asia",
      title: "Backpacking Southeast Asia",
      icon: <Mountain className="w-12 h-12" />,
      description: "Comprehensive protection for long-term travel through diverse environments and rural areas.",
      keyRisks: ["Typhoid", "Japanese Encephalitis", "Dengue", "Rabies"],
      image: "https://images.pexels.com/photos/1371360/pexels-photo-1371360.jpeg?auto=compress&cs=tinysrgb&w=800",
      gradient: "from-green-500 to-teal-600"
    },
    {
      id: "latin-america",
      title: "Latin America & Caribbean",
      icon: <Plane className="w-12 h-12" />,
      description: "Resort and adventure travel health planning for tropical and subtropical destinations.",
      keyRisks: ["Dengue", "Zika", "Travellers' Diarrhea", "Hepatitis A"],
      image: "https://images.pexels.com/photos/1450353/pexels-photo-1450353.jpeg?auto=compress&cs=tinysrgb&w=800",
      gradient: "from-purple-500 to-pink-600"
    },
    {
      id: "africa-safari",
      title: "Sub-Saharan Africa Safari",
      icon: <Globe className="w-12 h-12" />,
      description: "Essential preparation for safari adventures and volunteering in high-risk regions.",
      keyRisks: ["Yellow Fever", "Malaria", "Typhoid", "Rabies"],
      image: "https://images.pexels.com/photos/1670732/pexels-photo-1670732.jpeg?auto=compress&cs=tinysrgb&w=800",
      gradient: "from-yellow-600 to-red-600"
    },
    {
      id: "business-travel",
      title: "Business & Short Trips",
      icon: <Briefcase className="w-12 h-12" />,
      description: "Efficient health planning for time-conscious business travelers and short-stay visitors.",
      keyRisks: ["Routine Boosters", "Jet Lag", "Food Safety"],
      image: "https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=800",
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      id: "family-travel",
      title: "Family & Multi-Age Travel",
      icon: <Users className="w-12 h-12" />,
      description: "Comprehensive health planning for families, children, and multi-generational travel.",
      keyRisks: ["Pediatric Vaccines", "Pregnancy Safety", "Elderly Care"],
      image: "https://images.pexels.com/photos/1271619/pexels-photo-1271619.jpeg?auto=compress&cs=tinysrgb&w=800",
      gradient: "from-rose-500 to-pink-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Travel Health by <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Destination</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Every journey is unique. Get personalized health recommendations based on your specific destination, activities, and travel style.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white backdrop-blur-sm">
              <Heart className="w-5 h-5 text-cyan-400" />
              <span className="text-sm font-medium">Expert guidance for 100+ destinations worldwide</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trip Categories Grid */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tripCategories.map((trip, index) => (
              <motion.div
                key={trip.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/trip-types/${trip.id}`}
                  className="group block bg-white dark:bg-navy-900 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-105"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={trip.image} 
                      alt={trip.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${trip.gradient} opacity-60`} />
                    <div className="absolute top-4 right-4 w-16 h-16 bg-white dark:bg-navy-900 rounded-full flex items-center justify-center text-cyan-600 dark:text-cyan-400 shadow-lg">
                      {trip.icon}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-3 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                      {trip.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      {trip.description}
                    </p>

                    {/* Key Risks */}
                    <div className="mb-4">
                      <div className="text-xs font-bold text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-2">
                        Key Health Considerations
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {trip.keyRisks.slice(0, 3).map((risk, i) => (
                          <span 
                            key={i}
                            className="px-3 py-1 rounded-full bg-gray-100 dark:bg-navy-800 text-xs text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-white/10"
                          >
                            {risk}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400 font-medium group-hover:translate-x-2 transition-transform">
                      View detailed guide <ArrowRight size={18} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-r from-cyan-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-4">
            Not sure which category fits your trip?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Our travel health experts will help you create a personalized plan during your virtual consultation.
          </p>
          <button
            onClick={() => setIsBookingModalOpen(true)}
            className="inline-block px-10 py-4 bg-white text-blue-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
          >
            Book Virtual Consult
          </button>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default TripTypes;
